/* src/components/inbox/LeadListPane.jsx
 * Inbox list pane — search, status + quality filters, per-form browse dropdown.
 */

import React from 'react';
import { LeadRowChips } from './chips';
import { InboxListSkeleton } from '../Skeleton';

const STATUS_FILTERS = [
  { id: 'all', label: 'ALL' },
  { id: 'active', label: 'ACTIVE' },
  { id: 'booked', label: 'BOOKED' },
  { id: 'no_response', label: 'NO RESPONSE' },
  { id: 'needs_followup', label: 'NEEDS FOLLOW-UP' },
  { id: 'blocked', label: 'SPAM / BLOCKED' },
];

const QUALITY_FILTERS = [
  { id: 'all', label: 'ALL QUALITY' },
  { id: 'clean', label: 'CLEAN' },
  { id: 'suspicious', label: 'SUSPICIOUS' },
];

const ASSIGNMENT_FILTERS = [
  { id: 'all', label: 'ALL ASSIGNED' },
  { id: 'mine', label: 'MINE' },
  { id: 'unassigned', label: 'UNASSIGNED' },
];

function formatRowTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'YESTERDAY';
  return d.toLocaleDateString();
}

function lastMessagePreview(lead) {
  const history = lead.conversationHistory;
  if (Array.isArray(history) && history.length > 0) {
    const last = history[history.length - 1];
    const text = String(last?.message || '').replace(/<[^>]*>/g, '');
    return text.length > 90 ? `${text.slice(0, 90)}\u2026` : text;
  }
  return 'No messages yet';
}

export default function LeadListPane({
  leads,
  loading,
  selectedId,
  onSelect,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  qualificationFilter,
  onQualificationFilterChange,
  assignmentFilter,
  onAssignmentFilterChange,
  forms,
  formId,
  onFormChange,
  mobile,
}) {
  return (
    <section
      style={{
        width: mobile ? '100%' : '320px',
        flexShrink: 0,
        background: 'var(--surface-2)',
        borderRight: mobile ? 'none' : '1px solid var(--hairline-3)',
        height: mobile ? 'auto' : '100vh',
        position: mobile ? 'static' : 'sticky',
        top: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: '20px 18px 12px', borderBottom: '1px solid var(--hairline-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, fontStretch: '115%' }}>Inbox</h1>
          <span className="mono" style={{ fontSize: '11px', color: 'var(--dim)' }}>
            {leads.length} {leads.length === 1 ? 'lead' : 'leads'}
          </span>
        </div>

        {forms && forms.length > 0 && (
          <select
            value={formId || ''}
            onChange={(e) => onFormChange(e.target.value || null)}
            style={{
              marginTop: '12px',
              width: '100%',
              border: '1px solid var(--hairline-5)',
              background: 'var(--surface)',
              borderRadius: '8px',
              padding: '8px 10px',
              fontSize: '13px',
              color: 'var(--ink)',
              fontFamily: "'Archivo', sans-serif",
            }}
            aria-label="Browse by form"
          >
            <option value="">All forms</option>
            {forms.map((f) => (
              <option key={f.id} value={f.id}>{f.name || f.id}</option>
            ))}
          </select>
        )}

        <input
          type="search"
          placeholder="Search leads…"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            marginTop: '10px',
            width: '100%',
            boxSizing: 'border-box',
            border: '1px solid var(--hairline-5)',
            background: 'var(--surface)',
            borderRadius: '8px',
            padding: '8px 12px',
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '12px',
            color: 'var(--ink)',
          }}
        />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
          {STATUS_FILTERS.map((f) => (
            <span
              key={f.id}
              className={`inbox-filter-chip${statusFilter === f.id ? ' active' : ''}`}
              onClick={() => onStatusFilterChange(f.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onStatusFilterChange(f.id)}
            >
              {f.label}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
          {QUALITY_FILTERS.map((f) => (
            <span
              key={f.id}
              className={`inbox-filter-chip${qualificationFilter === f.id ? ' active' : ''}`}
              onClick={() => onQualificationFilterChange(f.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onQualificationFilterChange(f.id)}
            >
              {f.label}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
          {ASSIGNMENT_FILTERS.map((f) => (
            <span
              key={f.id}
              className={`inbox-filter-chip${assignmentFilter === f.id ? ' active' : ''}`}
              onClick={() => onAssignmentFilterChange && onAssignmentFilterChange(f.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onAssignmentFilterChange && onAssignmentFilterChange(f.id)}
            >
              {f.label}
            </span>
          ))}
        </div>
      </div>

      <div style={mobile ? undefined : { flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <InboxListSkeleton />
        ) : leads.length === 0 ? (
          <div style={{ padding: '2rem 18px', textAlign: 'center', color: 'var(--dim)', fontSize: '13px' }}>
            No leads match this filter.
          </div>
        ) : (
          leads.map((lead) => (
            <button
              key={lead.id}
              type="button"
              className={`inbox-row${selectedId === lead.id ? ' selected' : ''}`}
              onClick={() => onSelect(lead)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: selectedId === lead.id ? 700 : 600, color: 'var(--ink)' }}>
                  {lead.leadName || 'Unknown'}
                </span>
                <span className="mono" style={{ fontSize: '10px', color: 'var(--faint)', whiteSpace: 'nowrap' }}>
                  {formatRowTime(lead.lastMessageAt)}
                </span>
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--muted)',
                  marginTop: '3px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {lastMessagePreview(lead)}
              </div>
              <LeadRowChips lead={lead} />
            </button>
          ))
        )}
      </div>
    </section>
  );
}
